import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/landingTestIds";

const FREE_FEATURES = [
  "Basic CRM features",
  "Local lead management",
  "Standard pipeline tracking",
  "Up to 1 user",
  "Community support",
];

const PREMIUM_FEATURES = [
  "Advanced AI co-pilot",
  "Unlimited WhatsApp automation",
  "Custom landing page builder",
  "Priority support",
  "Everything in Free, plus more",
];

const PricingSection = () => {
  return (
    <section id="pricing" className="bg-[#0F172A] py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] bg-[#2563EB]/10 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#38bdf8]">
            Pricing
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Simple plans that grow with you
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Start free, upgrade whenever you're ready to unlock AI-powered automation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55 }}
            className="rounded-2xl border border-slate-700 bg-[#1E293B] p-8 sm:p-10 flex flex-col"
          >
            <h3 className="font-heading text-xl font-semibold text-white">Free Plan</h3>
            <p className="text-slate-400 text-sm mt-1">For agents just getting started</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-bold font-heading text-white">₹0</span>
              <span className="text-slate-400 text-sm">/ forever</span>
            </div>

            <ul className="mt-8 space-y-4 flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="w-4.5 h-4.5 w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-sm">{f}</span>
                </li>
              ))}
            </ul>

            <Button
              data-testid={LANDING.pricingFreeBtn}
              onClick={() =>
                document.querySelector("#download")?.scrollIntoView({ behavior: "smooth" })
              }
              className="mt-9 w-full bg-white/10 hover:bg-white/20 text-white border border-slate-600 rounded-lg h-12 font-semibold transition-all duration-300"
            >
              Get Started Free
            </Button>
          </motion.div>

          {/* Premium Plan */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="relative rounded-2xl p-[1px] bg-gradient-to-br from-[#38bdf8] via-[#2563EB] to-[#2dd4bf] shadow-2xl shadow-blue-500/20"
          >
            <div className="rounded-2xl bg-[#0F172A] p-8 sm:p-10 h-full flex flex-col relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#2563EB]/30 rounded-full blur-[80px]" />
              <div className="relative flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold text-white">Premium Plan</h3>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#38bdf8] to-[#2dd4bf] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0F172A]">
                  <Sparkles className="w-3 h-3" /> Most Powerful
                </span>
              </div>
              <p className="relative text-slate-400 text-sm mt-1">
                For agents ready to scale with AI
              </p>
              <div className="relative mt-6 flex items-baseline gap-1">
                <span className="text-4xl font-bold font-heading text-white">Contact Us</span>
              </div>
              <p className="relative text-xs text-slate-500 mt-1">Pricing coming soon</p>

              <ul className="relative mt-8 space-y-4 flex-1">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#2dd4bf]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#2dd4bf]" />
                    </div>
                    <span className="text-slate-200 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={LANDING.pricingPremiumBtn}
                onClick={() =>
                  (window.location.href = "mailto:support@tracreoneai.com?subject=Upgrade to Tracre One Premium")
                }
                className="relative mt-9 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-12 font-semibold shadow-lg shadow-blue-500/30 transition-all duration-300"
              >
                Upgrade to Premium
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
